import { z } from 'zod'

import { pageSchema } from './page'

export const callModeSchema = z.enum(['CLICK_TO_CALL', 'SIM'])
export const callPurposeSchema = z.enum(['SERVICE', 'PROMOTIONAL'])
export const callStatusSchema = z.enum(['INITIATED', 'DIALING', 'CONNECTED', 'ENDED'])
export const callOutcomeSchema = z.enum([
  'ANSWERED',
  'NO_ANSWER',
  'BUSY',
  'FAILED',
  'CANCELED',
  'AGENT_UNAVAILABLE',
  'NOT_CONNECTED',
  'UNKNOWN',
])
export const callProviderTypeSchema = z.enum(['EXOTEL', 'MOBILE_SIM'])
export const callDirectionSchema = z.enum(['OUTBOUND', 'INBOUND'])
export const callTrackingSourceSchema = z.enum(['CARRIER', 'DEVICE_LOG'])
export const dispositionAppliesToSchema = z.enum(['CONNECTED', 'NOT_CONNECTED', 'ANY'])

export type CallMode = z.infer<typeof callModeSchema>
export type CallPurpose = z.infer<typeof callPurposeSchema>
export type CallStatus = z.infer<typeof callStatusSchema>
export type CallOutcome = z.infer<typeof callOutcomeSchema>
export type CallProviderType = z.infer<typeof callProviderTypeSchema>
export type CallDirection = z.infer<typeof callDirectionSchema>
export type DispositionAppliesTo = z.infer<typeof dispositionAppliesToSchema>

// ─── Calls ────────────────────────────────────────────────────────────────────

export const callSchema = z.object({
  id: z.string().uuid(),
  recordId: z.string().uuid().nullable(),
  recordGroupId: z.string().uuid().nullable(),
  leadName: z.string().nullable(),
  leadNumber: z.string().nullable(),
  agentUserId: z.string().uuid().nullable(),
  agentName: z.string().nullable(),
  providerType: callProviderTypeSchema,
  direction: callDirectionSchema,
  purpose: callPurposeSchema,
  trackingSource: callTrackingSourceSchema,
  status: callStatusSchema,
  outcome: callOutcomeSchema.nullable(),
  requestedAt: z.string(),
  startedAt: z.string().nullable(),
  answeredAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  connectedDurationSec: z.number().int().nullable(),
  billableDurationSec: z.number().int().nullable(),
  hangupCause: z.string().nullable(),
  hasRecording: z.boolean(),
  recordingUrl: z.string().nullable(),
  dispositionId: z.string().uuid().nullable(),
  dispositionLabel: z.string().nullable(),
  dispositionColorHex: z.string().nullable(),
  dispositionNote: z.string().nullable(),
  disposedAt: z.string().nullable(),
  pushSentAt: z.string().nullable(),
  canDispose: z.boolean(),
})
export type Call = z.infer<typeof callSchema>

export const callPageSchema = pageSchema(callSchema)
export type CallPage = z.infer<typeof callPageSchema>

export const startCallRequestSchema = z.object({
  recordId: z.string().uuid(),
  phoneFieldId: z.string().uuid(),
  mode: callModeSchema.optional(),
  purpose: callPurposeSchema.optional(),
  /** SIM mode: true when this device dials (the app); false pushes "tap to call" to the agent's phone. */
  dialOnThisDevice: z.boolean().optional(),
  deviceId: z.string().uuid().optional(),
})
export type StartCallRequest = z.infer<typeof startCallRequestSchema>

export const setDispositionRequestSchema = z.object({
  dispositionId: z.string().uuid(),
  note: z.string().max(2000).optional(),
  followUpAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  followUpNote: z.string().max(500).optional(),
})
export type SetDispositionRequest = z.infer<typeof setDispositionRequestSchema>

export interface CallLogFilters {
  agentUserId?: string
  from?: string
  to?: string
  outcome?: CallOutcome
  dispositionId?: string
  providerType?: CallProviderType
  direction?: CallDirection
  unlinked?: boolean
}

// ─── Me / devices ─────────────────────────────────────────────────────────────

export const pushDeviceSchema = z.object({
  id: z.string().uuid(),
  platform: z.string(),
  deviceLabel: z.string().nullable(),
  appVersion: z.string().nullable(),
  simAccountId: z.string().nullable(),
  pushCapable: z.boolean(),
  lastSeenAt: z.string(),
})
export type PushDevice = z.infer<typeof pushDeviceSchema>

export const androidAppInfoSchema = z.object({
  apkUrl: z.string().nullable(),
  latestVersion: z.string(),
  minSupportedVersion: z.string(),
})
export type AndroidAppInfo = z.infer<typeof androidAppInfoSchema>

export const callingMeSchema = z.object({
  featureEnabled: z.boolean(),
  canMakeCalls: z.boolean(),
  canConfigure: z.boolean(),
  canViewAll: z.boolean(),
  providerConfigured: z.boolean(),
  modes: z.array(callModeSchema),
  purposes: z.array(callPurposeSchema),
  defaultPurpose: callPurposeSchema,
  defaultRegion: z.string(),
  verificationCallAvailable: z.boolean(),
  profile: z
    .object({
      phone: z.string().nullable(),
      verified: z.boolean(),
      verifiedAt: z.string().nullable(),
      preferredMode: callModeSchema,
      verificationRequestedAt: z.string().nullable(),
      verificationAnswered: z.boolean(),
    })
    .nullable(),
  devices: z.array(pushDeviceSchema),
  pushEnabled: z.boolean(),
  android: androidAppInfoSchema,
})
export type CallingMe = z.infer<typeof callingMeSchema>

export const agentPhoneRequestSchema = z.object({
  phone: z.string().max(32).optional(),
  preferredMode: callModeSchema.optional(),
})
export type AgentPhoneRequest = z.infer<typeof agentPhoneRequestSchema>

export const agentSchema = z.object({
  userId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  verified: z.boolean(),
  verifiedAt: z.string().nullable(),
  preferredMode: callModeSchema,
})
export type Agent = z.infer<typeof agentSchema>

export const deviceRegistrationRequestSchema = z.object({
  installationId: z.string().min(1).max(128),
  platform: z.enum(['ANDROID', 'IOS', 'WEB']),
  deviceLabel: z.string().max(255).optional(),
  fcmToken: z.string().max(4096).optional(),
  appVersion: z.string().max(32).optional(),
  simAccountId: z.string().max(255).optional(),
})
export type DeviceRegistrationRequest = z.infer<typeof deviceRegistrationRequestSchema>

// ─── Telephony settings ───────────────────────────────────────────────────────

export const webhookTokenSchema = z.object({
  token: z.string(),
  statusCallbackUrl: z.string(),
  inboundUrl: z.string(),
})
export type WebhookToken = z.infer<typeof webhookTokenSchema>

export const providerConfigSchema = z.object({
  id: z.string().uuid().nullable(),
  providerType: z.literal('EXOTEL'),
  enabled: z.boolean(),
  accountSid: z.string().nullable(),
  apiHost: z.string().nullable(),
  apiKeyMasked: z.string().nullable(),
  apiTokenSet: z.boolean(),
  callerIdService: z.string().nullable(),
  callerIdPromotional: z.string().nullable(),
  defaultPurpose: callPurposeSchema,
  recordCalls: z.boolean(),
  verificationAppId: z.string().nullable(),
  webhookConfigured: z.boolean(),
  statusWebhookUrlMasked: z.string().nullable(),
  inboundWebhookUrlMasked: z.string().nullable(),
  allowedApiHosts: z.array(z.string()),
  updatedAt: z.string().nullable(),
  newWebhook: webhookTokenSchema.nullable(),
})
export type ProviderConfig = z.infer<typeof providerConfigSchema>

export const providerConfigRequestSchema = z.object({
  enabled: z.boolean(),
  accountSid: z.string().min(1).max(255),
  apiHost: z.string().min(1).max(255),
  /** Blank keeps the stored key (it is only ever shown masked). */
  apiKey: z.string().max(255).optional(),
  apiToken: z.string().max(255).optional(),
  callerIdService: z.string().regex(/^(\+?[0-9]{6,15})?$/, 'Must be a phone number').optional(),
  callerIdPromotional: z.string().regex(/^(\+?[0-9]{6,15})?$/, 'Must be a phone number').optional(),
  defaultPurpose: callPurposeSchema,
  recordCalls: z.boolean(),
  verificationAppId: z.string().max(64).optional(),
})
export type ProviderConfigRequest = z.infer<typeof providerConfigRequestSchema>

export const connectionTestSchema = z.object({ ok: z.boolean(), message: z.string() })
export type ConnectionTest = z.infer<typeof connectionTestSchema>

export const dispositionSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  colorHex: z.string().nullable(),
  appliesTo: dispositionAppliesToSchema,
  requiresNote: z.boolean(),
  createsFollowUp: z.boolean(),
  sortOrder: z.number().int(),
  active: z.boolean(),
})
export type Disposition = z.infer<typeof dispositionSchema>

export const dispositionRequestSchema = z.object({
  label: z.string().min(1).max(100),
  colorHex: z.string().regex(/^(#[0-9a-fA-F]{6})?$/).optional(),
  appliesTo: dispositionAppliesToSchema,
  requiresNote: z.boolean(),
  createsFollowUp: z.boolean(),
  active: z.boolean().optional(),
})
export type DispositionRequest = z.infer<typeof dispositionRequestSchema>

// ─── SIM calling (Android app) ────────────────────────────────────────────────

export const deviceLogEntrySchema = z.object({
  callLogId: z.string().min(1),
  number: z.string().min(1),
  type: z.enum(['INCOMING', 'OUTGOING', 'MISSED', 'REJECTED']),
  startedAt: z.string(),
  durationSec: z.number().int().nonnegative(),
  simAccountId: z.string().optional(),
})
export type DeviceLogEntry = z.infer<typeof deviceLogEntrySchema>

export const deviceLogResponseSchema = z.object({ created: z.number().int(), skipped: z.number().int() })
